import React, { useState, useEffect } from 'react';
import { db, auth } from "../firebase"; 
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, serverTimestamp, arrayUnion } from 'firebase/firestore';
import { signOut } from "firebase/auth";
import "./AdminPanel.css";

export default function Dashboard({ setIsAuthenticated }) {
  const [albumName, setAlbumName] = useState('');
  const [pageType, setPageType] = useState('categorized');
  const [category, setCategory] = useState('editorial');
  const [imageFiles, setImageFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Existing albums management states
  const [albums, setAlbums] = useState([]);
  const [loadingAlbums, setLoadingAlbums] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [updatedAlbumName, setUpdatedAlbumName] = useState('');
  const [newImageFiles, setNewImageFiles] = useState([]); // Edit කරන විට අලුතින් දමන පින්තූර සඳහා
  const [updatingId, setUpdatingId] = useState(false);

  // Fetch all albums from Firestore
  const fetchAlbums = async () => {
    setLoadingAlbums(true);
    try {
      const categorizedSnapshot = await getDocs(collection(db, "categorized_albums"));
      const collectionSnapshot = await getDocs(collection(db, "collection_albums"));

      const categorizedList = categorizedSnapshot.docs.map(doc => ({
        id: doc.id,
        collectionName: 'categorized_albums',
        ...doc.data()
      }));

      const collectionList = collectionSnapshot.docs.map(doc => ({
        id: doc.id,
        collectionName: 'collection_albums',
        ...doc.data()
      }));

      setAlbums([...categorizedList, ...collectionList]);
    } catch (error) {
      console.error("Error fetching albums: ", error);
    } finally {
      setLoadingAlbums(false);
    }
  };

  useEffect(() => {
    fetchAlbums();
  }, []);

  // Optimized Upload Function using Batches to prevent 400 Bad Request errors
  const uploadImagesToCloudinary = async (filesToUpload) => {
    const cloudName = "shmjwwo4"; 
    const uploadPreset = "dilan_munasingha";
    const filesArray = Array.from(filesToUpload);
    
    const batchSize = 3; // එකවර ෆොටෝ 3ක් බැගින් අප්ලෝඩ් වේ
    let allUrls = [];

    for (let i = 0; i < filesArray.length; i += batchSize) {
      const batch = filesArray.slice(i, i + batchSize);
      
      const uploadPromises = batch.map(async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("upload_preset", uploadPreset);

        try {
          const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
            method: "POST",
            body: formData,
          });
          const data = await response.json();
          return data.secure_url;
        } catch (err) {
          console.error("Failed to upload image:", err);
          return null;
        }
      });

      const urls = await Promise.all(uploadPromises);
      allUrls.push(...urls.filter(url => url !== null));
    }

    return allUrls;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!albumName || imageFiles.length === 0) {
      alert("Please enter an album name and select images to upload.");
      return;
    }

    setUploading(true);
    try {
      const imageUrls = await uploadImagesToCloudinary(imageFiles);

      if (pageType === 'categorized') {
        await addDoc(collection(db, "categorized_albums"), {
          category: category,
          albumName: albumName,
          images: imageUrls,
          createdAt: serverTimestamp()
        });
      } else {
        await addDoc(collection(db, "collection_albums"), {
          albumName: albumName,
          images: imageUrls,
          createdAt: serverTimestamp()
        });
      }

      alert("Album uploaded successfully!");
      setAlbumName('');
      setImageFiles([]);
      fetchAlbums();
    } catch (error) {
      console.error("Error uploading: ", error);
      alert("An error occurred while uploading. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  // Update Album Name and Add New Images Handler
  const handleUpdate = async (id, collectionName) => {
    if (!updatedAlbumName) {
      alert("Please enter a new album name!");
      return;
    }

    setUpdatingId(true);
    try {
      const albumRef = doc(db, collectionName, id);
      let updateData = { albumName: updatedAlbumName };

      // අලුතින් පින්තූර තෝරා ඇත්නම් ඒවා Cloudinary වෙත අප්ලෝඩ් කර ෆයර්බේස් වෙත එකතු කිරීම
      if (newImageFiles.length > 0) {
        const newImageUrls = await uploadImagesToCloudinary(newImageFiles);
        
        // undefined හෝ null අගයන් සම්පූර්ණයෙන්ම ඉවත් කර valid URLs පමණක් තෝරා ගැනීම
        const validImageUrls = newImageUrls.filter(url => url);

        if (validImageUrls.length > 0) {
          updateData.images = arrayUnion(...validImageUrls);
        }
      }

      await updateDoc(albumRef, updateData);

      alert("Album updated successfully!");
      setEditingId(null);
      setUpdatedAlbumName('');
      setNewImageFiles([]);
      fetchAlbums();
    } catch (error) {
      console.error("Error updating album: ", error);
      alert("Failed to update album.");
    } finally {
      setUpdatingId(false);
    }
  };

  // Delete Album Handler
  const handleDelete = async (id, collectionName) => {
    if (window.confirm("Are you sure you want to delete this album?")) {
      try {
        await deleteDoc(doc(db, collectionName, id));
        alert("Album deleted successfully!");
        fetchAlbums();
      } catch (error) {
        console.error("Error deleting album: ", error);
        alert("Failed to delete album.");
      }
    }
  };

  // Logout Handler
  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.clear();
      setIsAuthenticated(false);
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return (
    <div className="admin-panel">
      <div className="admin-card" style={{ maxWidth: '850px', margin: '40px auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2>Dashboard - Upload & Manage Albums</h2>
          <button 
            onClick={handleLogout} 
            style={{ background: '#ff4d4d', color: 'white', border: 'none', padding: '8px 15px', borderRadius: '4px', cursor: 'pointer' }}
          >
            Logout
          </button>
        </div>

        {/* Upload Form */}
        <form onSubmit={handleSubmit} style={{ marginBottom: '40px', borderBottom: '1px solid #ddd', paddingBottom: '30px' }}>
          <h3>Upload New Album</h3>
          <div className="form-group">
            <label>Page Type:</label>
            <select value={pageType} onChange={(e) => setPageType(e.target.value)}>
              <option value="categorized">Categorized Page (Editorial, Wedding, etc.)</option>
              <option value="collection">Collection Page (Direct Albums)</option>
            </select>
          </div>

          {pageType === 'categorized' && (
            <div className="form-group">
              <label>Category:</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="latest">Latest</option>
                <option value="editorial">Editorial</option>
                <option value="commercial">Commercial</option>
                <option value="wedding">Wedding</option>
                <option value="portraits">Portraits</option>
              </select>
            </div>
          )}

          <div className="form-group">
            <label>Album Name:</label>
            <input 
              type="text" 
              value={albumName} 
              onChange={(e) => setAlbumName(e.target.value)} 
              placeholder="Enter album name"
              required 
            />
          </div>

          <div className="form-group">
            <label>Select Images:</label>
            <input 
              type="file" 
              multiple 
              accept="image/*" 
              onChange={(e) => setImageFiles(e.target.files)} 
              required 
            />
          </div>

          <button type="submit" disabled={uploading} className="submit-btn">
            {uploading ? "Uploading..." : "Upload Album"}
          </button>
        </form>

        {/* Manage Existing Albums Section */}
        <div>
          <h3>Existing Albums</h3>
          {loadingAlbums ? (
            <p>Loading albums...</p>
          ) : albums.length === 0 ? (
            <p>No albums found in database.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '15px' }}>
              {albums.map((album) => (
                <div key={album.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f9f9f9', padding: '12px 15px', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                  
                  {/* Left Side: Thumbnail, Name & Details */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: 1 }}>
                    {album.images && album.images.length > 0 ? (
                      <img 
                        src={album.images[0]} 
                        alt={album.albumName} 
                        style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px' }} 
                      />
                    ) : (
                      <div style={{ width: '50px', height: '50px', background: '#ccc', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px' }}>No Img</div>
                    )}

                    <div style={{ flex: 1 }}>
                      {editingId === album.id ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <input 
                            type="text" 
                            defaultValue={album.albumName}
                            onChange={(e) => setUpdatedAlbumName(e.target.value)}
                            style={{ padding: '5px' }}
                            placeholder="New album name"
                          />
                          <div>
                            <label style={{ fontSize: '12px', color: '#555', display: 'block', marginBottom: '3px' }}>Add More Images (Optional):</label>
                            <input 
                              type="file" 
                              multiple 
                              accept="image/*" 
                              onChange={(e) => setNewImageFiles(e.target.files)}
                              style={{ fontSize: '12px' }}
                            />
                          </div>
                        </div>
                      ) : (
                        <>
                          <h4 style={{ margin: '0 0 5px 0', color: '#333' }}>{album.albumName}</h4>
                          <small style={{ color: '#666' }}>
                            {album.collectionName === 'categorized_albums' ? `Categorized (${album.category})` : 'Collection'} | 📸 {album.images ? album.images.length : 0} Images
                          </small>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Right Side: Action Buttons */}
                  <div style={{ display: 'flex', gap: '8px', marginLeft: '15px' }}>
                    {editingId === album.id ? (
                      <>
                        <button 
                          onClick={() => handleUpdate(album.id, album.collectionName)}
                          disabled={updatingId}
                          style={{ background: '#28a745', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer' }}
                        >
                          {updatingId ? "Saving..." : "Save"}
                        </button>
                        <button 
                          onClick={() => { setEditingId(null); setNewImageFiles([]); }}
                          style={{ background: '#6c757d', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer' }}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button 
                          onClick={() => { setEditingId(album.id); setUpdatedAlbumName(album.albumName); setNewImageFiles([]); }}
                          style={{ background: '#ffc107', color: '#000', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => handleDelete(album.id, album.collectionName)}
                          style={{ background: '#dc3545', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer' }}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>

                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}